/**
 * VilaxFooter — branded footer for the Vilax Milano exhibition site.
 * Registered in the VLT "footer" slot via applyConfig.
 *
 * BUG-2026-093 fix (approach D — decouple logo from texture):
 *
 *   • Tile-texture background (vilax-footer-texture-tile.jpg, 767×767
 *     seamless embossed spiral pattern).  `background-repeat: repeat`
 *     means the container is ALWAYS covered in texture at every
 *     viewport height — no viewport-tall asset, no mask-fade-to-green,
 *     no aspect-ratio coupling.  Mobile-safe by construction.
 *
 *   • Separate logo overlay (vilax-footer-logo.png, 1060×574 — the
 *     gold Vilax script on its own oval-green cartouche, RGBA with
 *     transparent background around the oval).  Rendered as a plain
 *     centered <img> at the top of the footer flow; scales by width
 *     with a clamp() cap so it never becomes unreadably small or
 *     unreasonably large.
 *
 *   • Content (contact block + colophon) flows BELOW the logo in
 *     normal document flow — no padding-based logo avoidance, no
 *     pseudo-element geometry coupling.  Whatever the viewport width
 *     or height, content lands where it should relative to the logo.
 *
 * Supersedes the ffacae0 pseudo-element + mask-fade approach (rejected
 * 2026-04-19 because it treated the background as something that
 * faded into solid green — it's the texture throughout) and the
 * original Milano styling pass using a single composite 1924×940 image
 * with background-size:cover + padding-top:22% (which failed on mobile
 * portrait viewports — see BUG-2026-093 root-cause section).
 *
 * DR-2026-055 (original slot registration).
 * DR-2026-096 (colophon via voltolighttheme.footer behavior).
 * SPEC-SW-001 §3.5 → retracted; a future SPEC revision documents
 * approach D as the Vilax footer reference implementation.
 */
import { useSelector } from 'react-redux';
import { useLiveData } from '@kitconcept/volto-light-theme/helpers/useLiveData';
import footerLogo from '../../assets/vilax-footer-logo.png';
import footerTexture from '../../assets/vilax-footer-texture-tile.jpg';

const VILAX_GREEN = '#0b552a';
const VILAX_SAND = '#d4c9a8';

const FONT_STACK =
  "'Roboto Condensed', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const LABEL_STYLE = {
  fontSize: '0.875rem',
  fontWeight: 700,
  letterSpacing: '2px',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
};

const CONTACT_LINK_STYLE = {
  color: VILAX_SAND,
  textDecoration: 'none',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  fontStyle: 'inherit',
  letterSpacing: 'inherit',
  lineHeight: 'inherit',
  textTransform: 'inherit',
};

const VilaxFooter = () => {
  const content = useSelector((state) => state?.content?.data);
  const colophon = useLiveData(
    content,
    'voltolighttheme.footer',
    'footer_colophon_text',
  );

  return (
    <div
      aria-label="Vilax"
      style={{
        backgroundColor: VILAX_GREEN,
        backgroundImage: `url(${footerTexture})`,
        backgroundRepeat: 'repeat',
        // Natural tile size (767×767) keeps the embossed-pattern scale
        // that the asset was authored for. At typical viewports this
        // yields 2–3 repeats horizontally and 1–2 vertically, which
        // reads as texture rather than as visibly-tiled imagery.
        backgroundSize: 'auto',
        color: VILAX_SAND,
        fontFamily: FONT_STACK,
        paddingTop: 'clamp(2.5rem, 5vw, 4rem)',
        paddingBottom: 'clamp(2rem, 4vw, 3rem)',
        paddingLeft: '1.5rem',
        paddingRight: '1.5rem',
      }}
    >
      {/* Logo overlay */}
      <div
        style={{
          textAlign: 'center',
          marginBottom: 'clamp(1.5rem, 3vw, 2.5rem)',
        }}
      >
        <img
          src={footerLogo}
          alt="Vilax"
          style={{
            width: 'clamp(220px, 28vw, 380px)',
            height: 'auto',
            display: 'inline-block',
          }}
        />
      </div>

      {/* Contact block */}
      <div
        style={{
          maxWidth: '600px',
          margin: '0 auto',
          textAlign: 'center',
          fontSize: '1rem',
          lineHeight: 1.8,
          letterSpacing: '0.02em',
        }}
      >
        <div style={LABEL_STYLE}>Vilax AB</div>
        <div>Tirupsvägen 9</div>
        <div>245 93 Staffanstorp, Sweden</div>
        <div style={{ marginTop: '0.75rem' }}>
          <a href="tel:+46462345 67" style={CONTACT_LINK_STYLE}>
            +46 (0)46 – 23 45 67
          </a>
        </div>
        <div>
          <a href="mailto:info@vilax.se" style={CONTACT_LINK_STYLE}>
            info@vilax.se
          </a>
        </div>
      </div>

      {/* Colophon */}
      <div
        style={{
          borderTop: '1px solid rgba(212, 201, 168, 0.2)',
          marginTop: '2rem',
          paddingTop: '1.5rem',
          textAlign: 'center',
          fontSize: '1.125rem',
          fontWeight: 700,
          letterSpacing: '2px',
          textTransform: 'uppercase',
          color: 'rgba(212, 201, 168, 0.6)',
        }}
      >
        {colophon || 'Swedish beds since 1949'}
      </div>
    </div>
  );
};

export default VilaxFooter;
